import axios from "axios";

const API = axios.create({
  baseURL: import.meta.env.VITE_REACT_APP_BACKEND_BASEURL,
  timeout: 30000, // 30 segundos de timeout
});

// Interceptor para adicionar o token de autorização automaticamente
API.interceptors.request.use(
  (config) => {
    if (config.url) {
      // Admin
      if (
        config.url.includes("/api/parametros") ||
        config.url.includes("/athletes") ||
        config.url.includes("/teams")
      ) {
        const adminToken = localStorage.getItem("token");
        if (adminToken) {
          config.headers.Authorization = `Bearer ${adminToken}`;
        }
      }
      // Games: tenta admin, senão scouter
      else if (config.url.includes("/games")) {
        const adminToken = localStorage.getItem("token");
        const scouterToken = localStorage.getItem("scouterToken");
        if (adminToken) {
          config.headers.Authorization = `Bearer ${adminToken}`;
        } else if (scouterToken) {
          config.headers.Authorization = `Bearer ${scouterToken}`;
        }
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para lidar com a expiração de token ou usuário excluído
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Se for 401 (não autorizado), limpa token e redireciona
    if (error.response && error.response.status === 401) {
      // Opcional: mostrar mensagem amigável
      alert("Sua sessão expirou. Faça login novamente.");
      localStorage.removeItem("token");
      localStorage.removeItem("scouterToken");
      window.location.href = "/login";
      return Promise.reject(error);
    }

    // Tratamento específico para erros de conexão na primeira carga
    if (
      (error.code === "ECONNREFUSED" ||
        error.message.includes("Network Error") ||
        error.message.includes("connection refused")) &&
      !originalRequest._retry
    ) {
      console.log("🔄 Tentando reconectar...");
      originalRequest._retry = true;
      await new Promise((resolve) => setTimeout(resolve, 200));
      return API(originalRequest);
    }

    return Promise.reject(error);
  }
);

// Função utilitária para retry automático
const retryRequest = async (requestFn, maxRetries = 3, delay = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      const isConnectionError =
        error.code === "ECONNREFUSED" ||
        error.message.includes("Network Error") ||
        error.message.includes("connection refused") ||
        error.message.includes("timeout");

      if (isConnectionError && i < maxRetries - 1) {
        console.log(
          `🔄 Tentativa ${i + 1} falhou, tentando novamente em ${delay}ms...`
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }
      throw error;
    }
  }
};

// Funções originais (mantidas para compatibilidade)
export const getGames = () => API.get("/games");
export const getGame = (id) => API.get(`/games/${id}`);
export const createGame = (data) => API.post("/games", data);
export const updateGame = (id, data) => API.put(`/games/${id}`, data);
export const deleteGame = (id) => API.delete(`/games/${id}`);

export const getTeams = () => API.get("/teams"); // para listar os times
export const getTeamAthletes = (teamId) => API.get(`/teams/${teamId}`); // assumir que retorna os atletas

// Relatório de fundamentos por atleta
export const getAthleteFundamentalsReport = (partida, team) =>
  API.post("/reports/athletes-fundamentals", { partida, team });

// --- API para Atalhos de Teclado (Parâmetros) ---
export const getShortcuts = () => API.get("/api/parametros/atalhos");
export const addShortcut = (shortcutData) =>
  API.post("/api/parametros/atalhos", shortcutData);
export const updateShortcut = (originalKey, shortcutData) =>
  API.put(`/api/parametros/atalhos/${originalKey}`, shortcutData);
export const deleteShortcut = (originalKey) =>
  API.delete(`/api/parametros/atalhos/${originalKey}`);

// Função para atualizar código de um scout específico
export const updateScoutCode = (
  partidaId,
  scoutId,
  cod,
  tempo,
  combo,
  sz,
  ez,
  ez2
) =>
  API.put(`/matches/scout/update/${partidaId}/${scoutId}`, {
    cod,
    tempo,
    combo,
    sz,
    ez,
    ez2,
  });

// Função para buscar atalhos do admin logado
export const getAdminShortcuts = () => API.get("/api/parametros/atalhos");

// Wrapper para as funções de API com retry automático (alternativas)
export const getGamesWithRetry = () => retryRequest(() => API.get("/games"));
export const getGameWithRetry = (id) =>
  retryRequest(() => API.get(`/games/${id}`));
export const createGameWithRetry = (data) =>
  retryRequest(() => API.post("/games", data));
export const updateGameWithRetry = (id, data) =>
  retryRequest(() => API.put(`/games/${id}`, data));
export const deleteGameWithRetry = (id) =>
  retryRequest(() => API.delete(`/games/${id}`));

export const getTeamsWithRetry = () => retryRequest(() => API.get("/teams")); // para listar os times
export const getTeamAthletesWithRetry = (teamId) =>
  retryRequest(() => API.get(`/teams/${teamId}`)); // assumir que retorna os atletas

// Relatório de fundamentos por atleta
export const getAthleteFundamentalsReportWithRetry = (partida, team) =>
  retryRequest(() =>
    API.post("/reports/athletes-fundamentals", { partida, team })
  );

// --- API para Atalhos de Teclado (Parâmetros) ---
export const getShortcutsWithRetry = () =>
  retryRequest(() => API.get("/api/parametros/atalhos"));
export const addShortcutWithRetry = (shortcutData) =>
  retryRequest(() => API.post("/api/parametros/atalhos", shortcutData));
export const updateShortcutWithRetry = (originalKey, shortcutData) =>
  retryRequest(() =>
    API.put(`/api/parametros/atalhos/${originalKey}`, shortcutData)
  );
export const deleteShortcutWithRetry = (originalKey) =>
  retryRequest(() => API.delete(`/api/parametros/atalhos/${originalKey}`));

// Função para atualizar código de um scout específico
export const updateScoutCodeWithRetry = (
  partidaId,
  scoutId,
  cod,
  tempo,
  combo,
  sz,
  ez,
  ez2
) =>
  retryRequest(() =>
    API.put(`/matches/scout/update/${partidaId}/${scoutId}`, {
      cod,
      tempo,
      combo,
      sz,
      ez,
      ez2,
    })
  );

// Função para buscar atalhos do admin logado
export const getAdminShortcutsWithRetry = () =>
  retryRequest(() => API.get("/api/parametros/atalhos"));

// Exportar a instância API como default
export default API;
